import React, { useEffect, useRef } from 'react';
import { IHighlight, HighlightGroup } from './types';
import { useLocale } from 'locale';
import { FeedbackDropdown } from './FeedbackDropdown';
import { insightTypeToIcon } from 'helpers/insight';
import { H2, Text } from 'ds/components/typography';
import BackArrow from 'assets/svg/left.svg';
import { OverlayWrapper } from 'components/overlay-wrapper';
import { ProgressBar } from './ProgressBar';
import { HighlightStatus, Swipable, SwipableItem, SWIPE_TRANSITION_DURATION } from './Swipable';
import { InsightSummaryRenderer } from 'components/insight-summary-renderer';
import scrollIntoView from 'scroll-into-view';
import { ScrollSpyProvider, ScrollSpyTarget } from 'components/scroll-spy';
import { debounce } from 'lodash';
import { WithContentSlotSidePaddingContainer } from 'ds/components/Layout';
import {
  GroupLabelHeader,
  HighlightsMobileFooter,
  HighlightsMobileWrapper,
  MobileCloseButtonWrapper,
  MobileHighlightBody,
  MobileHighlightIconWrapper,
  MobileHighlightTitle,
  MobileHighlightWrapper,
} from './styled';
import BackLink from 'components/navigation/BackLink';
import { MobileMapWidget } from 'components/unit-page/mobile-map-widget';
import { State } from 'store/state';
import { connect } from 'react-redux';
import { IAutocompleteEntry, IBulletin, IProject } from 'utils/entities';
import { routeSelector } from 'store/state/selectors/router';
import { Route } from 'config/routes';
import { projectPageProjectSelector, addressDocEntrySelector, unitPageListingWithInsightsSelector } from 'store/state/domainData/selectors';
import { setNewUnitPageMapExpanded as setNewUnitPageMapExpandedAction  } from 'store/state/app/actions';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { isUnitPageRoute } from 'utils/marketplaceRoutes';


export const CloseButton: React.FunctionComponent = () => (
  <MobileCloseButtonWrapper>
    <BackLink>
      <BackArrow width={24} height={24} />
    </BackLink>
  </MobileCloseButtonWrapper>
);

interface HighlightInnerProps extends Pick<IHighlight, 'type' | 'text' | 'title' | 'subType' | 'id' | 'group'> {
  bulletin?: IBulletin;
  resolvedAddress?: IAutocompleteEntry;
  project?: IProject;
  routeWithMobileMap: boolean;
  isListingPage: boolean;
  setNewUnitPageMapExpanded: (expanded: boolean) => void;
}


const HighlightInnerBase: React.FunctionComponent<HighlightInnerProps> = React.memo(({
  type,
  group,
  subType,
  text,
  title,
  id,
  bulletin,
  resolvedAddress,
  project,
  routeWithMobileMap,
  isListingPage,
  setNewUnitPageMapExpanded,
}) => {

  const screenBreakpoint = useScreenBreakpoint();
  const isMobile = screenBreakpoint < 3;

  const useCustomMapAction = isMobile && isListingPage ? () => setNewUnitPageMapExpanded(true) : undefined;

  let locationPoint = bulletin
    ? bulletin.locationPoint
    : project
      ? project.locationPoint
      : { lat: 0, lng: 0 };
  if (resolvedAddress) {
    const [ lng, lat ] = resolvedAddress.location;
    locationPoint = {
      lat,
      lng,
    };
  }
  const withMobileMap = routeWithMobileMap && group !== HighlightGroup.BuildingPermit;
  return (
    <MobileHighlightWrapper>
      <MobileHighlightIconWrapper insightType={type}>
        {insightTypeToIcon[type]}
      </MobileHighlightIconWrapper>
      <WithContentSlotSidePaddingContainer>
        <MobileHighlightTitle insightType={type}>
          <H2>{title}</H2>
        </MobileHighlightTitle>
      </WithContentSlotSidePaddingContainer>
      {withMobileMap && locationPoint ? (
        <WithContentSlotSidePaddingContainer>
          <MobileMapWidget customMapAction={useCustomMapAction} hideTitle {...{ type, subType, locationPoint }} />
        </WithContentSlotSidePaddingContainer>
      ) : null}
      <WithContentSlotSidePaddingContainer>
        <MobileHighlightBody>
          <Text>
            <InsightSummaryRenderer type={subType} summary={text} />
          </Text>
        </MobileHighlightBody>
      </WithContentSlotSidePaddingContainer>
      <WithContentSlotSidePaddingContainer>
        <div>
          <FeedbackDropdown insightId={id} />
        </div>
      </WithContentSlotSidePaddingContainer>
    </MobileHighlightWrapper>
  );
});

const mapStateToProps = (state: State) => {
  return ({
    bulletin: unitPageListingWithInsightsSelector(state),
    resolvedAddress: addressDocEntrySelector(state),
    project: projectPageProjectSelector(state),
    routeWithMobileMap: [ Route.UnitPage, Route.ProjectPage, Route.AddressPage, Route.UnitPageCommercial, Route.ProjectPageCommercial ].includes(routeSelector(state).name),
    isListingPage: isUnitPageRoute(routeSelector(state).name),
  });
};

const HighlightInner = connect(mapStateToProps, { setNewUnitPageMapExpanded: setNewUnitPageMapExpandedAction })(HighlightInnerBase);

interface HighlightsMobileProps {
  highlights: IHighlight[];
  onChangeIndex: (nextIndex: number) => void;
  activeHighlightIndex: number;
  isCommercial: boolean;
}

export const HighlightsMobile: React.FunctionComponent<HighlightsMobileProps> = ({
  highlights,
  onChangeIndex,
  activeHighlightIndex,
  isCommercial,
}) => {
  const { t } = useLocale();
  const highlightsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlightsRef && highlightsRef.current) {
      scrollIntoView((highlightsRef.current.children[0].children[activeHighlightIndex] as HTMLElement), {
        time: 10,
        align: {
          top: 0,
        },
      });
    }
  }, [ highlightsRef.current ]);

  useEffect(() => {
    return () => {
      onSpyIndexChange.current.cancel();
    };
  }, []);

  const activeHighlight = highlights[activeHighlightIndex];
  const renderHighlight = (highlight: IHighlight, idx: number) => {
    return (
      <ScrollSpyTarget targetId={idx} key={highlight.id}>
        <HighlightInner
          id={highlight.id}
          group={highlight.group}
          type={highlight.type}
          text={highlight.text}
          subType={highlight.subType}
          title={highlight.title}
        />
      </ScrollSpyTarget>
    );
  };

  const changeIndexRef = useRef(onChangeIndex);
  changeIndexRef.current = onChangeIndex; // gotta update it so the reference doesn't go stale
  const onSpyIndexChange = useRef(debounce((nextIndex: string) => {
    changeIndexRef.current(+nextIndex);
  }, SWIPE_TRANSITION_DURATION, { trailing: true }));

  return (
    <OverlayWrapper>
      <HighlightsMobileWrapper>
        <GroupLabelHeader data-auto="mobile-opened-tradeoff-group-title">
          <CloseButton />
          <Text weight="medium">
            {t('expandedHighlight.highlightGroupLabel', {
              group: activeHighlight ? activeHighlight.group : null,
              isCommercial,
            })}
          </Text>
        </GroupLabelHeader>
        <div ref={highlightsRef} data-auto="mobile-opened-tradeoffs">
          <ScrollSpyProvider onChange={onSpyIndexChange.current}>
            {highlights.length > 1 ? (
              <Swipable>
                {highlights.map(renderHighlight)}
              </Swipable>
            ) : (
              <div>
                <SwipableItem swipeX={0} isTransitioning={false} status={HighlightStatus.Active} topOffset={null}>
                  {highlights.map(renderHighlight)}
                </SwipableItem>
              </div>
            )}
          </ScrollSpyProvider>
        </div>
        <HighlightsMobileFooter>
          {highlights.length > 1 ? (
            <ProgressBar
              highlights={highlights}
              outerRef={highlightsRef}
              activeHighlightIndex={activeHighlightIndex}
            />
          ) : null}
        </HighlightsMobileFooter>
      </HighlightsMobileWrapper>
    </OverlayWrapper>
  );
};
